import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clearStoredAuth, fetchCurrentUser, getStoredAuth, loginWithStrapi } from '../lib/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Autenticacion optimista: si hay un JWT guardado en localStorage entramos
  // INMEDIATAMENTE con esos datos (sin esperar a /users/me) y validamos el
  // token en segundo plano.
  //
  // Esto resuelve el bug de "pide login al cambiar de pagina": el Header
  // navega con <a href>, lo que recarga la app y reinicia el estado de React.
  // Si esperabamos a /users/me antes de autenticar, cualquier fallo transitorio
  // de red/CORS dejaba al usuario fuera. Ahora entra al instante con el token
  // guardado y solo lo cerramos si Strapi confirma que el token ya no sirve.
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const stored = getStoredAuth();
      if (!stored?.jwt) {
        if (!cancelled) setLoading(false);
        return;
      }

      // 1. Entrar de inmediato con lo que ya tenemos guardado.
      if (!cancelled) {
        setAuth({ jwt: stored.jwt, user: stored.user || null });
        setLoading(false);
      }

      // 2. Validar en background contra Strapi para refrescar el usuario.
      const { status, user } = await fetchCurrentUser(stored.jwt);
      if (cancelled) return;

      if (status === 'invalid') {
        // Strapi rechazo el token (401/403): expiro o fue revocado.
        clearStoredAuth();
        setAuth(null);
      } else if (status === 'ok' && user) {
        // Token valido: refrescamos los datos del usuario por si cambiaron.
        setAuth({ jwt: stored.jwt, user });
      }
      // status === 'network': no tocamos nada. El usuario ya esta dentro
      // con el token guardado y seguira navegando.
    };

    restoreSession();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (identifier, password) => {
    setError(null);
    try {
      const result = await loginWithStrapi(identifier, password);
      setAuth(result);
      return true;
    } catch (err) {
      // Distinguimos fallo de red de credenciales invalidas para dar
      // feedback claro al usuario.
      setError(err?.message === 'NETWORK_ERROR' ? 'NETWORK_ERROR' : 'INVALID_CREDENTIALS');
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    clearStoredAuth();
    setAuth(null);
    setError(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: Boolean(auth?.jwt),
        user: auth?.user || null,
        jwt: auth?.jwt || null,
        loading,
        error,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
